/**
 * Idle route prefetch — after the app is up, warm every lazy route module's
 * chunk in the background (staggered, on idle).
 *
 * WHY (BUG-13, "first click on + Nouveau… does nothing, second works"): the
 * router lazy-loads each route as its own chunk. Navigating to a not-yet-loaded
 * route keeps the previous page on screen while the chunk downloads, so a fast
 * first click can land BEFORE the target page has rendered its "+ Nouveau…"
 * control and appear to do nothing; the second click (chunk now in) works. It is
 * systematic across pages and never reproduces under automation (Playwright waits
 * for the control before clicking). Warming the chunk ahead of time removes the
 * download window entirely: navigation is instant and the control is interactive
 * on arrival.
 *
 * Uses the SAME dynamic import() specifiers the router uses, so Vite serves ONE
 * shared chunk — warming here makes the router's later lazy() resolve from cache.
 * Best-effort: any failure is swallowed (a stale/renamed chunk is already handled
 * by the vite:preloadError reload in main.tsx).
 */
import type { RouteObject } from 'react-router';

type LazyRoute = RouteObject & { lazy?: () => Promise<unknown>; children?: LazyRoute[] };

function collectLazy(routes: readonly LazyRoute[], acc: Array<() => Promise<unknown>>): void {
  for (const route of routes) {
    if (typeof route.lazy === 'function') acc.push(route.lazy);
    if (route.children) collectLazy(route.children as LazyRoute[], acc);
  }
}

const onIdle = (cb: () => void): void => {
  const ric = (
    window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }
  ).requestIdleCallback;
  if (typeof ric === 'function') ric(cb, { timeout: 3_000 });
  else window.setTimeout(cb, 300);
};

/** Warm all lazy route chunks on idle, a few at a time to avoid a request burst. */
export function prefetchAllRoutes(routes: readonly LazyRoute[]): void {
  const thunks: Array<() => Promise<unknown>> = [];
  collectLazy(routes, thunks);
  let i = 0;
  const step = (): void => {
    for (let n = 0; n < 4 && i < thunks.length; n += 1, i += 1) {
      void thunks[i]!().catch(() => {});
    }
    if (i < thunks.length) onIdle(step);
  };
  onIdle(step);
}
